import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApi } from "@/hooks/use-api";
import { useAppState } from "@/store/useApp";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

const AGENT_IN_USE_MESSAGE =
  "This agent is in use by an active assessment and cannot be deleted";

interface DeleteAgentDialogProps {
  agentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const DeleteAgentDialog = ({
  agentId,
  open,
  onOpenChange,
  onSuccess,
}: DeleteAgentDialogProps) => {
  const { deleteAgent } = useApi();
  const navigate = useNavigate();
  const assessments = useAppState((state) => state.assessments);
  const [isDeleting, setIsDeleting] = useState(false);

  const isAssignedToAssessment = useMemo(
    () =>
      assessments?.some((assessment) => assessment.agentId === agentId) ??
      false,
    [assessments, agentId]
  );

  const handleDelete = async () => {
    if (!agentId || isAssignedToAssessment) return;
    setIsDeleting(true);
    try {
      await deleteAgent(agentId);
      toast.success("Agent deleted successfully");
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/agents");
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("agent_in_use")) {
        toast.error(AGENT_IN_USE_MESSAGE);
      } else {
        toast.error("Failed to delete agent");
        console.error(error);
      }
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Agent</DialogTitle>
          <DialogDescription>
            {isAssignedToAssessment
              ? AGENT_IN_USE_MESSAGE
              : "Are you sure you want to delete this agent? This action cannot be undone."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            {isAssignedToAssessment ? "Close" : "Cancel"}
          </Button>
          {!isAssignedToAssessment && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

